import React, { useState } from 'react';
import MermaidDiagram from './MermaidDiagram';
import mermaid from 'mermaid';
import { nordThemeVariables } from '../config/nordTheme';
import './MermaidEditorPage.css';

const DEFAULT_CODE = `flowchart TD
    Start[Start] --> Process[Prozess durchführen]
    Process --> Decision{Bedingung erfüllt?}
    Decision -->|Ja| Success[Erfolg]
    Decision -->|Nein| Retry[Wiederholen]
    Retry --> Process
    Success --> Ende[Ende]`;

const MermaidEditorPage: React.FC = () => {
  const [mermaidCode, setMermaidCode] = useState(DEFAULT_CODE);
  const [layout, setLayout] = useState<'TD' | 'LR'>('TD');
  const [exportTheme, setExportTheme] = useState<'default' | 'dark' | 'base'>(() => {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    // Nord theme uses 'base' as closest match in Mermaid
    return currentTheme === 'nord' ? 'base' : currentTheme === 'dark' ? 'dark' : 'default';
  });

  // Automatically update exportTheme when global theme changes
  React.useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const currentTheme = document.documentElement.getAttribute('data-theme');
          const newExportTheme = currentTheme === 'nord' ? 'base' : currentTheme === 'dark' ? 'dark' : 'default';
          setExportTheme(newExportTheme);
          console.log(`🎨 Theme changed to: ${currentTheme}, exportTheme updated to: ${newExportTheme}`);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Sync layout state with code content
  React.useEffect(() => {
    const match = mermaidCode.match(/(flowchart|graph)\s+(TD|LR|TB|RL|BT)/i);
    if (match) {
      const detectedLayout = match[2].toUpperCase();
      // Normalize TB to TD and only support TD/LR
      if (detectedLayout === 'LR') {
        setLayout('LR');
      } else {
        setLayout('TD');
      }
    }
  }, [mermaidCode]);

  const handleLayoutToggle = () => {
    const newLayout = layout === 'TD' ? 'LR' : 'TD';
    setLayout(newLayout);

    // Update the code to use the new layout
    // Support both 'flowchart' and 'graph' keywords, with flexible whitespace
    const updatedCode = mermaidCode.replace(
      /(flowchart|graph)\s+(TD|LR|TB|RL|BT)/i,
      `$1 ${newLayout}`
    );

    // If no match was found, try to add it at the beginning
    if (updatedCode === mermaidCode) {
      console.log('⚠️ No flowchart/graph directive found, prepending...');
      setMermaidCode(`flowchart ${newLayout}\n${mermaidCode}`);
    } else {
      setMermaidCode(updatedCode);
    }
  };

  const renderDiagramWithTheme = async (theme: 'default' | 'dark' | 'base') => {
    const isNordTheme = theme === 'base';

    // Get the current SVG from the live preview (with all user modifications)
    const livePreview = document.querySelector('.preview-container .diagram-wrapper');
    if (livePreview) {
      const liveSvg = livePreview.querySelector('svg');
      if (liveSvg) {
        console.log('📸 Using live SVG with user modifications for export');

        // Clone the SVG so we don't modify the original
        const clonedSvg = liveSvg.cloneNode(true) as SVGSVGElement;

        // Add background based on theme
        const viewBox = clonedSvg.getAttribute('viewBox');
        let width, height, x, y;

        if (viewBox) {
          const parts = viewBox.split(/\s+/);
          x = parts[0];
          y = parts[1];
          width = parts[2];
          height = parts[3];
        } else {
          x = '0';
          y = '0';
          width = clonedSvg.getAttribute('width') || '800';
          height = clonedSvg.getAttribute('height') || '600';
        }

        // Create background rectangle with theme-specific color
        const bgRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bgRect.setAttribute('x', x);
        bgRect.setAttribute('y', y);
        bgRect.setAttribute('width', width);
        bgRect.setAttribute('height', height);

        if (isNordTheme) {
          bgRect.setAttribute('fill', '#2E3440'); // Nord background
          console.log(`✅ Added Nord background to cloned SVG`);
        } else if (theme === 'dark') {
          bgRect.setAttribute('fill', '#0d1117'); // Dark theme background
          console.log(`✅ Added dark background to cloned SVG`);
        } else {
          bgRect.setAttribute('fill', '#ffffff'); // Light theme background
          console.log(`✅ Added light background to cloned SVG`);
        }

        // Insert as first child
        clonedSvg.insertBefore(bgRect, clonedSvg.firstChild);

        // Serialize and return
        const serializer = new XMLSerializer();
        return serializer.serializeToString(clonedSvg);
      }
    }

    console.log('⚠️ No live SVG found, rendering fresh diagram');

    // Fallback: render fresh if no live preview exists
    mermaid.initialize({
      startOnLoad: false,
      theme: theme,
      themeVariables: isNordTheme ? nordThemeVariables : undefined,
      securityLevel: 'loose',
      fontFamily: isNordTheme ? nordThemeVariables.fontFamily : 'inherit',
      flowchart: {
        useMaxWidth: false,
        htmlLabels: true,
        curve: 'basis',
        padding: isNordTheme ? 30 : 15,
        nodeSpacing: isNordTheme ? 80 : 60,
        rankSpacing: isNordTheme ? 80 : 60,
        diagramPadding: isNordTheme ? 20 : 10
      }
    });

    const id = `export-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const { svg } = await mermaid.render(id, mermaidCode);

    // Simple approach: Find ALL text with "Ja"/"Nein" and style them
    if (isNordTheme) {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svg, 'image/svg+xml');

      console.log('🎨 Applying colored backgrounds to Ja/Nein labels in export...');

      // Find all <g> groups that might contain labels
      const allGroups = svgDoc.querySelectorAll('g');
      console.log(`Found ${allGroups.length} groups in SVG (export)`);

      // First, let's find all text elements to see what's in the SVG
      const allTextElements = svgDoc.querySelectorAll('text');
      console.log(`Found ${allTextElements.length} text elements (export)`);
      allTextElements.forEach((txt, idx) => {
        const content = txt.textContent?.trim() || '';
        if (content) {
          console.log(`Text ${idx} (export): "${content}"`);
        }
      });

      allGroups.forEach((group) => {
        const textElement = group.querySelector('text');
        if (textElement) {
          const text = textElement.textContent?.toLowerCase().trim() || '';

          if (text === 'ja') {
            console.log('✅ Found "Ja" (export) - styling...');
            const rect = group.querySelector('rect');
            if (rect) {
              // Get current dimensions
              const w = parseFloat(rect.getAttribute('width') || '0');
              const h = parseFloat(rect.getAttribute('height') || '0');
              const x = parseFloat(rect.getAttribute('x') || '0');
              const y = parseFloat(rect.getAttribute('y') || '0');

              console.log(`  Ja rect BEFORE (export): w=${w}, h=${h}, x=${x}, y=${y}`);

              rect.setAttribute('fill', '#88C0D0');
              rect.setAttribute('fill-opacity', '1');
              rect.setAttribute('stroke', '#88C0D0');
              rect.setAttribute('stroke-width', '3');
              rect.setAttribute('rx', '8');

              // Increase size
              const newW = Math.max(w + 40, 70);
              const newH = Math.max(h + 20, 36);
              const newX = x - 20;
              const newY = y - 10;

              rect.setAttribute('width', newW.toString());
              rect.setAttribute('height', newH.toString());
              rect.setAttribute('x', newX.toString());
              rect.setAttribute('y', newY.toString());

              console.log(`  Ja rect AFTER (export): w=${newW}, h=${newH}, x=${newX}, y=${newY}`);
            } else {
              console.log('  ❌ No rect found in group for "Ja" (export)');
            }

            textElement.setAttribute('fill', '#2E3440');
            textElement.setAttribute('font-weight', '700');
            textElement.setAttribute('font-size', '15px');
          }
          else if (text === 'nein') {
            console.log('❌ Found "Nein" (export) - styling...');
            const rect = group.querySelector('rect');
            if (rect) {
              // Get current dimensions
              const w = parseFloat(rect.getAttribute('width') || '0');
              const h = parseFloat(rect.getAttribute('height') || '0');
              const x = parseFloat(rect.getAttribute('x') || '0');
              const y = parseFloat(rect.getAttribute('y') || '0');

              console.log(`  Nein rect BEFORE (export): w=${w}, h=${h}, x=${x}, y=${y}`);

              rect.setAttribute('fill', '#5E81AC');
              rect.setAttribute('fill-opacity', '1');
              rect.setAttribute('stroke', '#5E81AC');
              rect.setAttribute('stroke-width', '3');
              rect.setAttribute('rx', '8');

              // Increase size
              const newW = Math.max(w + 40, 70);
              const newH = Math.max(h + 20, 36);
              const newX = x - 20;
              const newY = y - 10;

              rect.setAttribute('width', newW.toString());
              rect.setAttribute('height', newH.toString());
              rect.setAttribute('x', newX.toString());
              rect.setAttribute('y', newY.toString());

              console.log(`  Nein rect AFTER (export): w=${newW}, h=${newH}, x=${newX}, y=${newY}`);
            } else {
              console.log('  ❌ No rect found in group for "Nein" (export)');
            }

            textElement.setAttribute('fill', '#ECEFF4');
            textElement.setAttribute('font-weight', '700');
            textElement.setAttribute('font-size', '15px');
          }
        }
      });

      // Add Nord background color to the SVG
      const svgElement = svgDoc.querySelector('svg');
      if (svgElement) {
        // Get SVG dimensions
        const viewBox = svgElement.getAttribute('viewBox');
        let width, height, x, y;

        if (viewBox) {
          const parts = viewBox.split(/\s+/);
          x = parts[0];
          y = parts[1];
          width = parts[2];
          height = parts[3];
        } else {
          x = '0';
          y = '0';
          width = svgElement.getAttribute('width') || '800';
          height = svgElement.getAttribute('height') || '600';
        }

        // Create background rectangle with Nord background color
        const bgRect = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect');
        bgRect.setAttribute('x', x);
        bgRect.setAttribute('y', y);
        bgRect.setAttribute('width', width);
        bgRect.setAttribute('height', height);
        bgRect.setAttribute('fill', '#2E3440'); // Nord background color

        // Insert as first child so it's behind everything else
        svgElement.insertBefore(bgRect, svgElement.firstChild);

        console.log(`✅ Added Nord background (${width}x${height}) to SVG export`);
      }

      const serializer = new XMLSerializer();
      return serializer.serializeToString(svgDoc);
    }

    return svg;
  };

  const getThemeDisplayName = () => {
    const currentTheme = document.documentElement.getAttribute('data-theme');
    // Return the actual theme name for the filename
    return currentTheme || 'default';
  };

  const handleDownloadSVG = async () => {
    try {
      const svgData = await renderDiagramWithTheme(exportTheme);
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mermaid_diagram_${getThemeDisplayName()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Fehler beim Erstellen des SVG. Bitte überprüfen Sie Ihren Mermaid-Code.');
    }
  };

  const handleDownloadPNG = async () => {
    try {
      const svgData = await renderDiagramWithTheme(exportTheme);

      // Parse SVG to get dimensions
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgData, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg') as SVGSVGElement;

      if (!svgElement) {
        alert('Fehler beim Erstellen des Diagramms');
        return;
      }

      const viewBox = svgElement.viewBox.baseVal;
      const width = viewBox.width || parseFloat(svgElement.getAttribute('width') || '800');
      const height = viewBox.height || parseFloat(svgElement.getAttribute('height') || '600');

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const scale = 2; // Higher resolution
      canvas.width = width * scale;
      canvas.height = height * scale;

      // Create an image from SVG
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        // Background color based on theme
        if (exportTheme === 'dark') {
          ctx.fillStyle = '#0d1117';
        } else if (exportTheme === 'base') {
          ctx.fillStyle = '#2E3440'; // Nord background
        } else {
          ctx.fillStyle = 'white';
        }
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const pngUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = pngUrl;
            link.download = `mermaid_diagram_${getThemeDisplayName()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(pngUrl);
          }
        }, 'image/png');

        URL.revokeObjectURL(url);
      };

      img.src = url;
    } catch (error) {
      console.error('Download error:', error);
      alert('Fehler beim Erstellen des PNG. Bitte überprüfen Sie Ihren Mermaid-Code.');
    }
  };

  return (
    <div className="mermaid-editor-page">
      <div className="editor-header">
        <h2>Mermaid Flowchart Editor</h2>
        <div className="editor-controls">
          <button
            className={`layout-toggle ${layout === 'TD' ? 'active' : ''}`}
            onClick={handleLayoutToggle}
            title="Layout umschalten"
          >
            {layout === 'TD' ? '↓ Vertikal' : '→ Horizontal'}
          </button>
          <div className="theme-selector-container">
            <label htmlFor="export-theme" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Export-Theme:
            </label>
            <select
              id="export-theme"
              value={exportTheme}
              onChange={(e) => setExportTheme(e.target.value as 'default' | 'dark' | 'base')}
              style={{
                padding: '0.5rem',
                background: 'var(--bg-primary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value="default">☀️ Light</option>
              <option value="dark">🌙 Dark</option>
              <option value="base">❄️ Nord</option>
            </select>
          </div>
          <button
            className="btn-download"
            onClick={handleDownloadSVG}
            title="Als SVG herunterladen"
          >
            📥 SVG
          </button>
          <button
            className="btn-download"
            onClick={handleDownloadPNG}
            title="Als PNG herunterladen"
          >
            📥 PNG
          </button>
        </div>
      </div>

      <div className="editor-content">
        <div className="editor-panel">
          <div className="panel-header">
            <h3>Mermaid Code</h3>
            <div className="code-info">
              <span className="info-badge">
                Layout: {layout === 'TD' ? 'Vertikal (↓)' : 'Horizontal (→)'}
              </span>
            </div>
          </div>
          <textarea
            value={mermaidCode}
            onChange={(e) => setMermaidCode(e.target.value)}
            className="code-editor"
            placeholder="flowchart TD&#10;    Start[Start] --> Ende[Ende]"
            spellCheck={false}
          />
          <div className="editor-help">
            <details>
              <summary>📚 Syntax-Hilfe</summary>
              <div className="help-content">
                <h4>Knoten-Typen:</h4>
                <ul>
                  <li><code>A[Rechteck]</code> - Standard-Prozessschritt</li>
                  <li><code>B{'{'}'Raute{'}'}</code> - Entscheidung/Verzweigung</li>
                  <li><code>C([Abgerundet])</code> - Start/Ende</li>
                </ul>
                <h4>Verbindungen:</h4>
                <ul>
                  <li><code>A --&gt; B</code> - Einfacher Pfeil</li>
                  <li><code>A --&gt;|Text| B</code> - Pfeil mit Label</li>
                  <li><code>A ---&gt; B</code> - Dicker Pfeil</li>
                </ul>
                <h4>Layouts:</h4>
                <ul>
                  <li><code>flowchart TD</code> - Von oben nach unten</li>
                  <li><code>flowchart LR</code> - Von links nach rechts</li>
                </ul>
              </div>
            </details>
          </div>
        </div>

        <div className="preview-panel">
          <div className="panel-header">
            <h3>Live-Vorschau</h3>
          </div>
          <div className="preview-container">
            <MermaidDiagram code={mermaidCode} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MermaidEditorPage;
